/**
 * UN NOM PROCHE DANS LE CARNET — module PUR (16/10/2026).
 *
 * À la validation d'une demande d'accès, la RQ doit savoir si l'institution
 * déclarée existe DÉJÀ au carnet, sous une graphie un peu différente :
 * « Labo Ben Ali » / « Laboratoire Ben Ali — Sousse », « Clinique El Amen » /
 * « Clinique Elamen ». Une égalité stricte ne voit rien de tout cela, et le
 * carnet se remplit de doublons que plus personne ne rapproche.
 *
 * On ne décide pas ici : on PROPOSE, classé par ressemblance, et la RQ tranche
 * — relier à un contact proposé, ou créer un nouveau contact.
 *
 * Mesure : mots communs (après retrait des mots creux : laboratoire, clinique,
 * centre, de, du…) ET bigrammes de caractères (Dice), ce qui attrape les
 * fautes de frappe et les mots collés. Seuil volontairement bas : une
 * proposition de trop se refuse d'un clic, un doublon se paie des mois.
 */

const CREUX = new Set([
  'laboratoire', 'laboratoires', 'labo', 'lab', 'analyses', 'analyse', 'medicales', 'medicale', 'biologie', 'medicale',
  'clinique', 'centre', 'cabinet', 'polyclinique', 'hopital', 'dr', 'docteur', 'pr', 'professeur',
  'de', 'du', 'des', 'la', 'le', 'les', 'l', 'd', 'et', 'a', 'au', 'aux', 'en', 'sa', 'sarl', 'suarl',
]);

export function normaliserNom(s: unknown): string {
  return String(s ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’`]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function mots(s: string): string[] {
  return normaliserNom(s).split(' ').filter((m) => m && !CREUX.has(m));
}

function bigrammes(s: string): Map<string, number> {
  const t = normaliserNom(s).replace(/ /g, '');
  const m = new Map<string, number>();
  for (let i = 0; i + 1 < t.length; i += 1) {
    const b = t.slice(i, i + 2);
    m.set(b, (m.get(b) || 0) + 1);
  }
  return m;
}

/** Coefficient de Dice sur les bigrammes de caractères : 0 (rien) à 1 (identique). */
export function dice(a: string, b: string): number {
  const ma = bigrammes(a); const mb = bigrammes(b);
  let commun = 0; let na = 0; let nb = 0;
  for (const v of ma.values()) na += v;
  for (const v of mb.values()) nb += v;
  if (!na || !nb) return 0;
  for (const [k, v] of ma) commun += Math.min(v, mb.get(k) || 0);
  return (2 * commun) / (na + nb);
}

/** Ressemblance de 0 à 1 entre deux noms d'institution. */
export function ressemblance(a: string, b: string): number {
  const na = normaliserNom(a); const nb = normaliserNom(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  const wa = mots(a); const wb = mots(b);
  /* Tout se mesure sur ce qui RESTE une fois les mots creux retirés : un nom
     réduit à « Laboratoire » ne ressemble à aucun laboratoire en particulier.
     Si l'un des deux n'a rien de propre et l'autre si, ils n'ont rien en commun. */
  if (wa.length !== wb.length && (!wa.length || !wb.length)) return 0;
  const ta = wa.length ? wa.join(' ') : na;
  const tb = wb.length ? wb.join(' ') : nb;
  let motsCommuns = 0;
  if (wa.length && wb.length) {
    const sb = new Set(wb);
    const communs = wa.filter((w) => sb.has(w)).length;
    motsCommuns = communs / Math.min(wa.length, wb.length);
  }
  /* L'un contenu dans l'autre (« Ben Ali » dans « Ben Ali Sousse ») pèse comme
     une correspondance de mots entière. */
  const inclus = (ta.length >= 4 && tb.includes(ta)) || (tb.length >= 4 && ta.includes(tb)) ? 1 : 0;
  return Math.max(dice(ta, tb), 0.85 * motsCommuns, 0.9 * inclus);
}

export interface LigneCarnet { code: string; nom: string; actif?: boolean }
export interface Candidat extends LigneCarnet { score: number; exact: boolean }

/** Seuil en deçà duquel on ne propose rien : la RQ créera. */
export const SEUIL = 0.5;

/**
 * Les lignes du carnet dont le nom ressemble à `nom`, de la plus proche à la
 * moins proche, au plus `max`. `exact` = même nom à la casse, aux accents et
 * à la ponctuation près.
 */
export function nomsProches(nom: string, lignes: readonly LigneCarnet[], max = 3): Candidat[] {
  const cible = normaliserNom(nom);
  if (!cible) return [];
  return lignes
    .map((l) => ({ ...l, score: ressemblance(nom, l.nom), exact: normaliserNom(l.nom) === cible }))
    .filter((c) => c.score >= SEUIL)
    .sort((a, b) => b.score - a.score || a.nom.localeCompare(b.nom))
    .slice(0, max);
}
